import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Priority Rules questions
export const priorityRulesMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-priority-1',
    text: {
      en: 'You are approaching a junction where a tram is also approaching from your right. Who has priority?',
      nl: 'Je nadert een kruising waar ook een tram van rechts nadert. Wie heeft voorrang?',
      ar: 'أنت تقترب من تقاطع حيث يقترب ترام أيضا من يمينك. من له الأولوية؟'
    },
    options: [
      {
        id: 'q-priority-1o1',
        text: {
          en: 'You have priority because you are on the main road',
          nl: 'Je hebt voorrang omdat je op de hoofdweg rijdt',
          ar: 'لديك الأولوية لأنك على الطريق الرئيسي'
        }
      },
      {
        id: 'q-priority-1o2',
        text: {
          en: 'The tram has priority, you must give way',
          nl: 'De tram heeft voorrang, je moet voorrang verlenen',
          ar: 'الترام له الأولوية، يجب عليك إعطاء الأولوية'
        }
      },
      {
        id: 'q-priority-1o3',
        text: {
          en: 'Neither, you both have equal priority',
          nl: 'Geen van beiden, jullie hebben gelijke voorrang',
          ar: 'لا أحد، لديكما الأولوية بالتساوي'
        }
      }
    ],
    correctAnswerId: 'q-priority-1o2',
    explanation: {
      en: 'Trams generally have priority over other vehicles, especially when they are on tracks. You must give way to trams at junctions.',
      nl: 'Trams hebben over het algemeen voorrang op andere voertuigen, vooral wanneer ze op rails rijden. Je moet voorrang verlenen aan trams bij kruisingen.',
      ar: 'للترام عادة الأولوية على المركبات الأخرى، خاصة عندما تكون على القضبان. يجب عليك إعطاء الأولوية للترام عند التقاطعات.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-2',
    text: {
      en: 'When turning left at a junction, who must you give way to?',
      nl: 'Bij linksaf slaan op een kruising, aan wie moet je voorrang verlenen?',
      ar: 'عند الانعطاف يسارا عند تقاطع، لمن يجب أن تعطي الأولوية؟'
    },
    options: [
      {
        id: 'q-priority-2o1',
        text: {
          en: 'Traffic approaching from the opposite direction going straight or turning right',
          nl: 'Verkeer dat van de tegenovergestelde richting komt en rechtdoor gaat of rechtsaf slaat',
          ar: 'المرور القادم من الاتجاه المعاكس الذي يسير مباشرة أو ينعطف يمينا'
        }
      },
      {
        id: 'q-priority-2o2',
        text: {
          en: 'Only pedestrians crossing the road',
          nl: 'Alleen voetgangers die de weg oversteken',
          ar: 'المشاة فقط الذين يعبرون الطريق'
        }
      },
      {
        id: 'q-priority-2o3',
        text: {
          en: 'No one, you have priority when turning left',
          nl: 'Niemand, je hebt voorrang bij linksaf slaan',
          ar: 'لا أحد، لديك الأولوية عند الانعطاف يسارا'
        }
      }
    ],
    correctAnswerId: 'q-priority-2o1',
    explanation: {
      en: 'When turning left at a junction, you must give way to traffic approaching from the opposite direction that is going straight ahead or turning right. You must also give way to pedestrians crossing the road you are turning into.',
      nl: 'Bij linksaf slaan op een kruising moet je voorrang verlenen aan verkeer dat van de tegenovergestelde richting komt en rechtdoor gaat of rechtsaf slaat. Je moet ook voorrang verlenen aan voetgangers die de weg oversteken waar je naartoe draait.',
      ar: 'عند الانعطاف يسارا عند تقاطع، يجب أن تعطي الأولوية للمرور القادم من الاتجاه المعاكس الذي يسير مباشرة أو ينعطف يمينا. يجب عليك أيضا إعطاء الأولوية للمشاة الذين يعبرون الطريق الذي تنعطف إليه.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-3',
    text: {
      en: 'At an unmarked crossroads, which vehicle has priority?',
      nl: 'Bij een ongemarkeerd kruispunt, welk voertuig heeft voorrang?',
      ar: 'عند تقاطع غير محدد، أي مركبة لها الأولوية؟'
    },
    options: [
      {
        id: 'q-priority-3o1',
        text: {
          en: 'The vehicle approaching from the right',
          nl: 'Het voertuig dat van rechts nadert',
          ar: 'المركبة القادمة من اليمين'
        }
      },
      {
        id: 'q-priority-3o2',
        text: {
          en: 'The larger vehicle',
          nl: 'Het grotere voertuig',
          ar: 'المركبة الأكبر'
        }
      },
      {
        id: 'q-priority-3o3',
        text: {
          en: 'The vehicle turning left',
          nl: 'Het voertuig dat linksaf slaat',
          ar: 'المركبة التي تنعطف يسارا'
        }
      }
    ],
    correctAnswerId: 'q-priority-3o1',
    explanation: {
      en: 'At an unmarked crossroads, the "priority to the right" rule applies. Vehicles approaching from the right have priority.',
      nl: 'Bij een ongemarkeerd kruispunt geldt de regel "voorrang van rechts". Voertuigen die van rechts naderen hebben voorrang.',
      ar: 'عند تقاطع غير محدد، تنطبق قاعدة "الأولوية لليمين". المركبات القادمة من اليمين لها الأولوية.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-4',
    text: {
      en: 'What does a "Give Way" sign (inverted triangle) mean?',
      nl: 'Wat betekent een "Voorrang verlenen" bord (omgekeerde driehoek)?',
      ar: 'ماذا يعني علامة "أعط الأولوية" (مثلث مقلوب)؟'
    },
    options: [
      {
        id: 'q-priority-4o1',
        text: {
          en: 'You must stop completely before proceeding',
          nl: 'Je moet volledig stoppen voordat je doorgaat',
          ar: 'يجب أن تتوقف تماما قبل المتابعة'
        }
      },
      {
        id: 'q-priority-4o2',
        text: {
          en: 'You must slow down and give way to traffic on the priority road',
          nl: 'Je moet vertragen en voorrang verlenen aan verkeer op de voorrangsweg',
          ar: 'يجب أن تبطئ وتعطي الأولوية للمرور على الطريق ذي الأولوية'
        }
      },
      {
        id: 'q-priority-4o3',
        text: {
          en: 'You have priority over all other traffic',
          nl: 'Je hebt voorrang op al het andere verkeer',
          ar: 'لديك الأولوية على جميع المرور الآخر'
        }
      }
    ],
    correctAnswerId: 'q-priority-4o2',
    explanation: {
      en: 'A "Give Way" sign means you must slow down and be prepared to stop if necessary to give way to traffic on the priority road.',
      nl: 'Een "Voorrang verlenen" bord betekent dat je moet vertragen en bereid moet zijn om indien nodig te stoppen om voorrang te verlenen aan verkeer op de voorrangsweg.',
      ar: 'تعني علامة "أعط الأولوية" أنه يجب عليك التباطؤ والاستعداد للتوقف إذا لزم الأمر لإعطاء الأولوية للمرور على الطريق ذي الأولوية.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-5',
    text: {
      en: 'At a T-junction, which vehicle typically has priority?',
      nl: 'Bij een T-kruising, welk voertuig heeft meestal voorrang?',
      ar: 'عند تقاطع T، أي مركبة عادة لها الأولوية؟'
    },
    options: [
      {
        id: 'q-priority-5o1',
        text: {
          en: 'The vehicle on the through road',
          nl: 'Het voertuig op de doorgaande weg',
          ar: 'المركبة على الطريق المستقيم'
        }
      },
      {
        id: 'q-priority-5o2',
        text: {
          en: 'The vehicle turning into the junction',
          nl: 'Het voertuig dat de kruising opdraait',
          ar: 'المركبة التي تدخل التقاطع'
        }
      },
      {
        id: 'q-priority-5o3',
        text: {
          en: 'The vehicle approaching from the left',
          nl: 'Het voertuig dat van links nadert',
          ar: 'المركبة القادمة من اليسار'
        }
      }
    ],
    correctAnswerId: 'q-priority-5o1',
    explanation: {
      en: 'At a T-junction, vehicles on the through road (the top of the T) typically have priority over vehicles turning into the junction.',
      nl: 'Bij een T-kruising hebben voertuigen op de doorgaande weg (de bovenkant van de T) meestal voorrang op voertuigen die de kruising opdraaien.',
      ar: 'عند تقاطع T، المركبات على الطريق المستقيم (أعلى T) عادة لها الأولوية على المركبات التي تدخل التقاطع.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-6',
    text: {
      en: 'What is the priority rule for emergency vehicles with flashing lights and sirens?',
      nl: 'Wat is de voorrangsregel voor hulpdiensten met knipperende lichten en sirenes?',
      ar: 'ما هي قاعدة الأولوية لمركبات الطوارئ ذات الأضواء الوامضة والصفارات؟'
    },
    options: [
      {
        id: 'q-priority-6o1',
        text: {
          en: 'They must follow normal traffic rules',
          nl: 'Ze moeten de normale verkeersregels volgen',
          ar: 'يجب عليهم اتباع قواعد المرور العادية'
        }
      },
      {
        id: 'q-priority-6o2',
        text: {
          en: 'They have priority over all other traffic and you must give way',
          nl: 'Ze hebben voorrang op al het andere verkeer en je moet voorrang verlenen',
          ar: 'لديهم الأولوية على جميع المرور الآخر ويجب عليك إعطاء الأولوية'
        }
      },
      {
        id: 'q-priority-6o3',
        text: {
          en: 'They only have priority at traffic lights',
          nl: 'Ze hebben alleen voorrang bij verkeerslichten',
          ar: 'لديهم الأولوية فقط عند إشارات المرور'
        }
      }
    ],
    correctAnswerId: 'q-priority-6o2',
    explanation: {
      en: 'Emergency vehicles with flashing lights and sirens have priority over all other traffic. You must give way and allow them to pass safely.',
      nl: 'Hulpdiensten met knipperende lichten en sirenes hebben voorrang op al het andere verkeer. Je moet voorrang verlenen en hen veilig laten passeren.',
      ar: 'مركبات الطوارئ ذات الأضواء الوامضة والصفارات لها الأولوية على جميع المرور الآخر. يجب عليك إعطاء الأولوية والسماح لهم بالمرور بأمان.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-7',
    animationId: 'priority-anim-5',
    text: {
      en: 'You want to enter a roundabout. A car is already circulating on it. What must you do?',
      nl: 'U wilt een rotonde oprijden. Een auto rijdt al op de rotonde. Wat moet u doen?',
      ar: 'تريد دخول الدوار. سيارة تسير بالفعل عليه. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-priority-7o1',
        text: {
          en: 'Enter immediately — entering traffic has priority at Dutch roundabouts',
          nl: 'Meteen oprijden — invoegend verkeer heeft voorrang bij Nederlandse rotondes',
          ar: 'ادخل فورا — للمركبات المندمجة الأولوية في الدوارات الهولندية'
        }
      },
      {
        id: 'q-priority-7o2',
        text: {
          en: 'Give way to the circulating car — it has priority',
          nl: 'Voorrang geven aan de auto op de rotonde — die heeft voorrang',
          ar: 'أعط الأولوية للسيارة الدائرة — لها الأولوية'
        }
      },
      {
        id: 'q-priority-7o3',
        text: {
          en: 'Priority to the right applies — compare positions',
          nl: 'Voorrang van rechts geldt — posities vergelijken',
          ar: 'تنطبق الأولوية لليمين — قارن المواضع'
        }
      }
    ],
    correctAnswerId: 'q-priority-7o2',
    explanation: {
      en: 'In the Netherlands, vehicles already on the roundabout have priority. Haaientanden (shark-teeth markings) at the entry confirm you must give way. Enter only when the roundabout is clear.',
      nl: 'In Nederland heeft verkeer dat al op de rotonde rijdt voorrang. Haaientanden (haaientandmarkering) bij de inrit bevestigen dat u voorrang moet verlenen. Rijdt u de rotonde pas op als die vrij is.',
      ar: 'في هولندا، للمركبات الموجودة بالفعل في الدوار الأولوية. علامات أسنان القرش (haaientanden) عند المدخل تؤكد أنك ملزم بالإذعان. ادخل الدوار فقط عندما يكون خاليا.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-8',
    text: {
      en: 'What does a "Stop" sign (octagonal red sign) require you to do?',
      nl: 'Wat vereist een "Stop" bord (achthoekig rood bord) dat je doet?',
      ar: 'ماذا تتطلب علامة "قف" (علامة حمراء مثمنة) منك أن تفعل؟'
    },
    options: [
      {
        id: 'q-priority-8o1',
        text: {
          en: 'Slow down and proceed if the way is clear',
          nl: 'Vertragen en doorgaan als de weg vrij is',
          ar: 'التباطؤ والمتابعة إذا كان الطريق خاليا'
        }
      },
      {
        id: 'q-priority-8o2',
        text: {
          en: 'Stop completely and give way to all traffic',
          nl: 'Volledig stoppen en voorrang verlenen aan al het verkeer',
          ar: 'التوقف تماما وإعطاء الأولوية لجميع المرور'
        }
      },
      {
        id: 'q-priority-8o3',
        text: {
          en: 'Stop only if other vehicles are present',
          nl: 'Alleen stoppen als andere voertuigen aanwezig zijn',
          ar: 'التوقف فقط إذا كانت هناك مركبات أخرى'
        }
      }
    ],
    correctAnswerId: 'q-priority-8o2',
    explanation: {
      en: 'A "Stop" sign requires you to stop completely at the stop line and give way to all traffic on the priority road before proceeding.',
      nl: 'Een "Stop" bord vereist dat je volledig stopt bij de stopstreep en voorrang verleent aan al het verkeer op de voorrangsweg voordat je doorgaat.',
      ar: 'تتطلب علامة "قف" التوقف تماما عند خط التوقف وإعطاء الأولوية لجميع المرور على الطريق ذي الأولوية قبل المتابعة.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-9',
    text: {
      en: 'You are driving on a main road and approach a side road on your right. A vehicle is waiting to join from the side road. Who has priority?',
      nl: 'Je rijdt op een hoofdweg en nadert een zijweg aan je rechterkant. Een voertuig wacht om vanaf de zijweg in te voegen. Wie heeft voorrang?',
      ar: 'أنت تقود على طريق رئيسي وتقترب من طريق جانبي على يمينك. مركبة تنتظر للانضمام من الطريق الجانبي. من له الأولوية؟'
    },
    options: [
      {
        id: 'q-priority-9o1',
        text: {
          en: 'You have priority as you are on the main road',
          nl: 'Je hebt voorrang omdat je op de hoofdweg rijdt',
          ar: 'لديك الأولوية لأنك على الطريق الرئيسي'
        }
      },
      {
        id: 'q-priority-9o2',
        text: {
          en: 'The vehicle from the side road has priority',
          nl: 'Het voertuig van de zijweg heeft voorrang',
          ar: 'المركبة من الطريق الجانبي لها الأولوية'
        }
      },
      {
        id: 'q-priority-9o3',
        text: {
          en: 'Priority depends on who arrived first',
          nl: 'Voorrang hangt af van wie het eerst aankwam',
          ar: 'تعتمد الأولوية على من وصل أولا'
        }
      }
    ],
    correctAnswerId: 'q-priority-9o1',
    explanation: {
      en: 'Vehicles on the main road have priority over vehicles joining from side roads. The vehicle from the side road must wait and give way to traffic on the main road.',
      nl: 'Voertuigen op de hoofdweg hebben voorrang op voertuigen die vanaf zijwegen invoegen. Het voertuig van de zijweg moet wachten en voorrang verlenen aan verkeer op de hoofdweg.',
      ar: 'المركبات على الطريق الرئيسي لها الأولوية على المركبات التي تنضم من الطرق الجانبية. يجب على المركبة من الطريق الجانبي الانتظار وإعطاء الأولوية للمرور على الطريق الرئيسي.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority-10',
    text: {
      en: 'You are approaching a junction where a yellow diamond-shaped sign with a white border is displayed. What does this sign indicate?',
      nl: 'Je nadert een kruispunt waar een geel ruitvormig bord met een witte rand wordt getoond. Wat betekent dit bord?',
      ar: 'أنت تقترب من تقاطع حيث يتم عرض علامة صفراء على شكل ماسي مع حدود بيضاء. ماذا تشير هذه العلامة؟'
    },
    options: [
      {
        id: 'q-priority-10o1',
        text: {
          en: 'You must give way to traffic from other directions',
          nl: 'Je moet voorrang verlenen aan verkeer uit andere richtingen',
          ar: 'يجب عليك إعطاء الأولوية للمرور من الاتجاهات الأخرى'
        }
      },
      {
        id: 'q-priority-10o2',
        text: {
          en: 'This is a roundabout ahead',
          nl: 'Dit is een rotonde vooruit',
          ar: 'هذه دوار قادم'
        }
      },
      {
        id: 'q-priority-10o3',
        text: {
          en: 'You have priority over traffic from other directions',
          nl: 'Je hebt voorrang op verkeer uit andere richtingen',
          ar: 'لديك الأولوية على المرور من الاتجاهات الأخرى'
        }
      }
    ],
    correctAnswerId: 'q-priority-10o3',
    explanation: {
      en: 'A yellow diamond-shaped sign with a white border indicates that you are on a priority road and have the right of way over traffic from other directions. Traffic from side roads must give way to you.',
      nl: 'Een geel ruitvormig bord met een witte rand geeft aan dat je op een voorrangsweg rijdt en voorrang hebt op verkeer uit andere richtingen. Verkeer van zijwegen moet jou voorrang verlenen.',
      ar: 'العلامة الصفراء على شكل ماسي مع حدود بيضاء تشير إلى أنك على طريق ذي أولوية ولديك حق الأولوية على المرور من الاتجاهات الأخرى. يجب على المرور من الطرق الجانبية إعطاء الأولوية لك.'
    },
    subject: 'Priority Rules'
  },
  {
    id: 'q-priority2-1',
    text: {
      en: 'You are on a priority road that ends at a junction. A "Give way" sign appears. Who has priority now?',
      nl: 'U rijdt op een voorrangsweg die bij een kruising eindigt. Er verschijnt een „Voorrang verlenen"-bord. Wie heeft nu voorrang?',
      ar: 'أنت على طريق ذي أولوية ينتهي عند تقاطع. تظهر إشارة «أعط الأولوية». من لديه الأولوية الآن؟',
    },
    options: [
      {
        id: 'q-priority2-1o1',
        text: {
          en: 'You still have priority until you leave the junction',
          nl: 'U heeft nog voorrang totdat u de kruising verlaat',
          ar: 'ما زالت لديك الأولوية حتى تغادر التقاطع',
        },
      },
      {
        id: 'q-priority2-1o2',
        text: {
          en: 'You must give way to traffic on the road you are joining',
          nl: 'U moet voorrang verlenen aan het verkeer op de weg waar u op invoegt',
          ar: 'يجب أن تمنح الأولوية للمرور على الطريق الذي تندمج إليه',
        },
      },
      {
        id: 'q-priority2-1o3',
        text: {
          en: 'Priority to the right applies only',
          nl: 'Alleen voorrang van rechts is van toepassing',
          ar: 'تنطبق الأولوية للقادم من اليمين فقط',
        },
      },
    ],
    correctAnswerId: 'q-priority2-1o2',
    explanation: {
      en: 'When your priority road ends, the Give way sign means you must yield to traffic on the road you are joining.',
      nl: 'Als uw voorrangsweg eindigt, betekent het bord Voorrang verlenen dat u het verkeer op de weg waar u op invoegt voorrang moet verlenen.',
      ar: 'عندما ينتهي طريقك ذو الأولوية، تعني إشارة «أعط الأولوية» أن يجب أن تمنح الأولوية للمرور على الطريق الذي تندمج إليه.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-2',
    text: {
      en: 'You are turning right at a junction. A cyclist is going straight ahead on the road you are crossing. Who has priority?',
      nl: 'U slaat rechtsaf bij een kruising. Een fietser rijdt rechtdoor op de weg die u kruist. Wie heeft voorrang?',
      ar: 'تنعطف يمينا عند تقاطع. راكب دراجة يسير مستقيما على الطريق الذي تعبره. من لديه الأولوية؟',
    },
    options: [
      {
        id: 'q-priority2-2o1',
        text: {
          en: 'You have priority when turning right',
          nl: 'U heeft voorrang bij rechts afslaan',
          ar: 'لك الأولوية عند الانعطاف يمينا',
        },
      },
      {
        id: 'q-priority2-2o2',
        text: {
          en: 'The cyclist going straight has priority; you must give way',
          nl: 'De fietser die rechtdoor gaat heeft voorrang; u moet voorrang verlenen',
          ar: 'راكب الدراجة المار مستقيما له الأولوية؛ يجب أن تمنح الأولوية',
        },
      },
      {
        id: 'q-priority2-2o3',
        text: {
          en: 'Whoever arrived first has priority',
          nl: 'Wie het eerst arriveert, heeft voorrang',
          ar: 'من وصل أولا له الأولوية',
        },
      },
    ],
    correctAnswerId: 'q-priority2-2o2',
    explanation: {
      en: 'When turning right, you must give way to oncoming traffic going straight ahead, including cyclists.',
      nl: 'Bij rechts afslaan moet u tegenliggers die rechtdoor gaan voorrang verlenen, waaronder fietsers.',
      ar: 'عند الانعطاف يمينا يجب أن تمنح الأولوية للمرور القادم من الاتجاه المعاكس المار مستقيما، بما في ذلك راكبي الدراجات.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-3',
    text: {
      en: 'At an unmarked junction, you and another vehicle arrive at the same time. The other vehicle is on your right. What should you do?',
      nl: 'Op een ongemarkeerde kruising komt u en een ander voertuig tegelijk aan. Het andere voertuig bevindt zich aan uw rechterzijde. Wat moet u doen?',
      ar: 'عند تقاطع غير معلم، تصل أنت ومركبة أخرى في الوقت نفسه. المركبة الأخرى على يمينك. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-priority2-3o1',
        text: {
          en: 'Proceed; you have priority as you are going straight',
          nl: 'Doorrijden; u heeft voorrang omdat u rechtdoor gaat',
          ar: 'تابع؛ لك الأولوية لأنك تسير مستقيما',
        },
      },
      {
        id: 'q-priority2-3o2',
        text: {
          en: 'Give way to the vehicle on your right',
          nl: 'Voorrang verlenen aan het voertuig aan uw rechterzijde',
          ar: 'أعط الأولوية للمركبة على يمينك',
        },
      },
      {
        id: 'q-priority2-3o3',
        text: {
          en: 'Sound your horn and proceed',
          nl: 'Claxonneren en doorrijden',
          ar: 'استخدم البوق وتابع',
        },
      },
    ],
    correctAnswerId: 'q-priority2-3o2',
    explanation: {
      en: 'At an unmarked junction, priority to the right applies. You must give way to the vehicle approaching from your right.',
      nl: 'Op een ongemarkeerde kruising geldt voorrang van rechts. U moet voorrang verlenen aan het voertuig dat van rechts nadert.',
      ar: 'عند تقاطع غير معلم تنطبق الأولوية للقادم من اليمين. يجب أن تمنح الأولوية للمركبة القادمة من يمينك.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-4',
    text: {
      en: 'A tram is approaching from your left at a junction. There are no signs or road markings. Who has priority?',
      nl: 'Een tram nadert van links op een kruising. Er zijn geen borden of wegmarkeringen. Wie heeft voorrang?',
      ar: 'ترام يقترب من يسارك عند تقاطع. لا توجد لافتات أو علامات على الطريق. من لديه الأولوية؟',
    },
    options: [
      {
        id: 'q-priority2-4o1',
        text: {
          en: 'You have priority; trams give way to the right',
          nl: 'U heeft voorrang; trams verlenen voorrang aan rechts',
          ar: 'لك الأولوية؛ تمنح الترامات الأولوية لمن على اليمين',
        },
      },
      {
        id: 'q-priority2-4o2',
        text: {
          en: 'The tram has priority; you must give way',
          nl: 'De tram heeft voorrang; u moet voorrang verlenen',
          ar: 'للترام الأولوية؛ يجب أن تمنح الأولوية',
        },
      },
      {
        id: 'q-priority2-4o3',
        text: {
          en: 'Priority to the right applies; compare positions',
          nl: 'Voorrang van rechts geldt; vergelijk de posities',
          ar: 'تنطبق الأولوية للقادم من اليمين؛ قارن المواقع',
        },
      },
    ],
    correctAnswerId: 'q-priority2-4o2',
    explanation: {
      en: 'Trams have priority over other traffic at junctions unless signs or signals indicate otherwise. You must give way to the tram.',
      nl: 'Trams hebben op kruisingen voorrang op overig verkeer, tenzij borden of signalen anders aangeven. U moet de tram voorrang verlenen.',
      ar: 'للترامات أولوية على غيرها من المرور عند التقاطعات ما لم تشير اللافتات أو الإشارات إلى غير ذلك. يجب أن تمنح الأولوية للترام.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-5',
    text: {
      en: 'You are leaving a private driveway to join a road. Who has priority?',
      nl: 'U verlaat een particuliere oprit om op een weg in te voegen. Wie heeft voorrang?',
      ar: 'تغادر مدخلا خاصا للاندماج في طريق. من لديه الأولوية؟',
    },
    options: [
      {
        id: 'q-priority2-5o1',
        text: {
          en: 'You have priority when joining',
          nl: 'U heeft voorrang bij het invoegen',
          ar: 'لك الأولوية عند الاندماج',
        },
      },
      {
        id: 'q-priority2-5o2',
        text: {
          en: 'Traffic on the road has priority; you must give way',
          nl: 'Het verkeer op de weg heeft voorrang; u moet voorrang verlenen',
          ar: 'للمرور على الطريق الأولوية؛ يجب أن تمنح الأولوية',
        },
      },
      {
        id: 'q-priority2-5o3',
        text: {
          en: 'Pedestrians only; then you may join',
          nl: 'Alleen voetgangers; daarna mag u invoegen',
          ar: 'المشاة فقط؛ ثم يجوز لك الاندماج',
        },
      },
    ],
    correctAnswerId: 'q-priority2-5o2',
    explanation: {
      en: 'When joining a road from a driveway or private property, you must give way to all traffic and pedestrians on that road.',
      nl: 'Wanneer u vanaf een oprit of particulier terrein op een weg invoegt, moet u alle verkeer en voetgangers op die weg voorrang verlenen.',
      ar: 'عند الاندماج في طريق من مدخل أو ملك خاص، يجب أن تمنح الأولوية لجميع المرور والمشاة على ذلك الطريق.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-6',
    text: {
      en: 'You are on a road with a continuous centre line. A vehicle is emerging from a field on your right. Who has priority?',
      nl: 'U rijdt op een weg met een doorgetrokken middenlijn. Een voertuig komt vanaf een veld aan uw rechterzijde op de rijbaan. Wie heeft voorrang?',
      ar: 'أنت على طريق فيه خط وسط متصل. مركبة تخرج من حقل على يمينك. من لديه الأولوية؟',
    },
    options: [
      {
        id: 'q-priority2-6o1',
        text: {
          en: 'You have priority; they are joining from private land',
          nl: 'U heeft voorrang; zij komen van particulier terrein',
          ar: 'لك الأولوية؛ هم يندمجون من أرض خاصة',
        },
      },
      {
        id: 'q-priority2-6o2',
        text: {
          en: 'They have priority as they are on your right',
          nl: 'Zij hebben voorrang omdat zij aan uw rechterzijde zijn',
          ar: 'لهم الأولوية لأنهم على يمينك',
        },
      },
      {
        id: 'q-priority2-6o3',
        text: {
          en: 'Priority to the right applies; give way to them',
          nl: 'Voorrang van rechts geldt; verleen hun voorrang',
          ar: 'تنطبق الأولوية للقادم من اليمين؛ أعطهم الأولوية',
        },
      },
    ],
    correctAnswerId: 'q-priority2-6o1',
    explanation: {
      en: 'A vehicle joining from a field or private access must give way to traffic already on the road. Priority to the right applies to public roads, not to vehicles entering from off-road.',
      nl: 'Een voertuig dat vanuit een veld of particuliere toegangsweg invoegt moet verkeer dat al op de weg rijdt voorrang verlenen. Voorrang van rechts geldt voor de openbare weg, niet voor voertuigen die van buiten de rijbaan komen.',
      ar: 'مركبة تندمج من حقل أو مدخل خاص يجب أن تمنح الأولوية للمرور الموجود بالفعل على الطريق. الأولوية للقادم من اليمين تنطبق على الطرق العامة، لا على المركبات الداخلة من خارج الطريق.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-7',
    text: {
      en: 'You approach a junction with a "Stop" sign. You have stopped. When may you proceed?',
      nl: 'U nadert een kruising met een stopbord. U bent gestopt. Wanneer mag u verder rijden?',
      ar: 'تقترب من تقاطع عليه إشارة «توقف». لقد توقفت. متى يجوز لك المضي؟',
    },
    options: [
      {
        id: 'q-priority2-7o1',
        text: {
          en: 'As soon as you have stopped',
          nl: 'Zodra u hebt stilgestaan',
          ar: 'بمجرد أن تتوقف',
        },
      },
      {
        id: 'q-priority2-7o2',
        text: {
          en: 'When you have given way to all traffic on the priority road',
          nl: 'Wanneer u alle verkeer op de voorrangsweg voorrang hebt verleend',
          ar: 'عندما تكون قد منحت الأولوية لجميع المرور على طريق الأولوية',
        },
      },
      {
        id: 'q-priority2-7o3',
        text: {
          en: 'When no vehicles are visible',
          nl: 'Wanneer er geen voertuigen zichtbaar zijn',
          ar: 'عندما لا ترى مركبات',
        },
      },
    ],
    correctAnswerId: 'q-priority2-7o2',
    explanation: {
      en: 'At a Stop sign you must stop and may only proceed when you can do so without affecting priority traffic.',
      nl: 'Bij een stopbord moet u stoppen en mag u alleen verder rijden wanneer u dat kunt zonder het verkeer met voorrang te hinderen.',
      ar: 'عند إشارة «توقف» يجب التوقف ولا يجوز المضي إلا عندما يمكن ذلك دون التأثير على مرور ذي أولوية.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-8',
    text: {
      en: 'You are turning left. A pedestrian is crossing the side road you are turning into. Who has priority?',
      nl: 'U slaat linksaf. Een voetganger steekt de zijweg over waarin u linksaf slaat. Wie heeft voorrang?',
      ar: 'تنعطف يسارا. أحد المشاة يعبر الطريق الفرعي الذي تنعطف إليه. من لديه الأولوية؟',
    },
    options: [
      {
        id: 'q-priority2-8o1',
        text: {
          en: 'You have priority; pedestrians must wait',
          nl: 'U heeft voorrang; voetgangers moeten wachten',
          ar: 'لك الأولوية؛ على المشاة الانتظار',
        },
      },
      {
        id: 'q-priority2-8o2',
        text: {
          en: 'The pedestrian has priority; you must give way',
          nl: 'De voetganger heeft voorrang; u moet voorrang verlenen',
          ar: 'للمشاة الأولوية؛ يجب أن تمنح الأولوية',
        },
      },
      {
        id: 'q-priority2-8o3',
        text: {
          en: 'Whoever reaches the crossing first',
          nl: 'Wie het eerst de oversteek bereikt',
          ar: 'من يصل إلى المعبر أولا',
        },
      },
    ],
    correctAnswerId: 'q-priority2-8o2',
    explanation: {
      en: 'When turning into a side road, you must give way to pedestrians who are crossing or about to cross that road.',
      nl: 'Bij het linksaf slaan naar een zijweg moet u voetgangers voorrang verlenen die die weg oversteken of op het punt staan die over te steken.',
      ar: 'عند الانعطاف إلى طريق فرعي، يجب أن تمنح الأولوية للمشاة الذين يعبرون ذلك الطريق أو على وشك عبوره.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-9',
    text: {
      en: 'Two roads of equal importance meet at an unmarked junction. No one is on your right. May you proceed?',
      nl: 'Twee wegen van gelijk belang komen samen op een ongemarkeerde kruising. Niemand bevindt zich aan uw rechterzijde. Mag u doorrijden?',
      ar: 'يلتقي طريان متساويان في الأهمية عند تقاطع غير معلم. لا أحد على يمينك. هل يجوز لك المضي؟',
    },
    options: [
      {
        id: 'q-priority2-9o1',
        text: {
          en: 'Yes; you have priority',
          nl: 'Ja; u heeft voorrang',
          ar: 'نعم؛ لك الأولوية',
        },
      },
      {
        id: 'q-priority2-9o2',
        text: {
          en: 'Only if no vehicle is approaching from the right',
          nl: 'Alleen als er geen voertuig van rechts nadert',
          ar: 'فقط إذا لم تكن مركبة تقترب من اليمين',
        },
      },
      {
        id: 'q-priority2-9o3',
        text: {
          en: 'You must always stop first',
          nl: 'U moet altijd eerst stoppen',
          ar: 'يجب أن تتوقف دائما أولا',
        },
      },
    ],
    correctAnswerId: 'q-priority2-9o2',
    explanation: {
      en: 'At an unmarked junction of equal roads, you may proceed when you do not have to give way to traffic from your right.',
      nl: 'Op een ongemarkeerde kruising van gelijkwaardige wegen mag u doorrijden wanneer u geen voorrang hoeft te verlenen aan verkeer van rechts.',
      ar: 'عند تقاطع غير معلم لطرق متكافئة، يجوز لك المضي عندما لا يتعين عليك منح الأولوية للمرور القادم من يمينك.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-10',
    animationId: null,
    text: {
      en: 'You are driving in a built-up area. A bus at a marked bus stop on your right has its indicator on and is about to pull out. What must you do?',
      nl: 'U rijdt in de bebouwde kom. Een bus bij een aangeduide bushalte rechts van u heeft zijn richtingaanwijzer aan en staat op het punt weg te rijden. Wat moet u doen?',
      ar: 'تقود داخل منطقة مبنية. حافلة عند محطة حافلات معلمة على يمينك تضيء مؤشرها وعلى وشك الانطلاق. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-priority2-10o1',
        text: {
          en: 'Maintain your speed — you have priority on the road',
          nl: 'Uw snelheid handhaven — u heeft voorrang op de weg',
          ar: 'حافظ على سرعتك — لك الأولوية على الطريق',
        },
      },
      {
        id: 'q-priority2-10o2',
        text: {
          en: 'Give way to the bus — it has priority when signalling to leave a bus stop within a built-up area',
          nl: 'Voorrang verlenen aan de bus — de bus heeft voorrang als hij aangeeft weg te rijden vanuit een bushalte in de bebouwde kom',
          ar: 'أعط الأولوية للحافلة — لها الأولوية عند الإشارة لمغادرة محطة الحافلات داخل المنطقة المبنية',
        },
      },
      {
        id: 'q-priority2-10o3',
        text: {
          en: 'Sound your horn to alert the bus driver you are there',
          nl: 'Claxonneer om de buschauffeur te waarschuwen dat u er bent',
          ar: 'انبه بالبوق لتنبيه سائق الحافلة بوجودك',
        },
      },
    ],
    correctAnswerId: 'q-priority2-10o2',
    explanation: {
      en: 'Within a built-up area (bebouwde kom), you must give way to buses leaving a marked bus stop when they signal (RVV 1990 art. 50). Slow down and let the bus out safely.',
      nl: 'Binnen de bebouwde kom bent u verplicht voorrang te verlenen aan lijnbussen die vanaf een bushalte willen wegrijden en dat kenbaar maken (RVV 1990 art. 50). Rem af en laat de bus veilig wegrijden.',
      ar: 'داخل المنطقة المبنية (bebouwde kom) يجب أن تمنح الأولوية للحافلات المغادرة لمحطة حافلات معلمة عند إعطائها الإشارة (RVV 1990 المادة 50). أبطئ واترك الحافلة تغادر بأمان.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-11',
    text: {
      en: 'At a junction you see a yellow diamond sign. You then see a "Give way" sign. What should you do?',
      nl: 'Op een kruising ziet u een geel ruitvormig bord. Daarna ziet u een „Voorrang verlenen"-bord. Wat moet u doen?',
      ar: 'عند تقاطع ترى إشارة الماسة الصفراء. ثم ترى إشارة «أعط الأولوية». ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-priority2-11o1',
        text: {
          en: 'The diamond overrides the Give way; you have priority',
          nl: 'Het ruitvormige bord prevaleert boven Voorrang verlenen; u heeft voorrang',
          ar: 'الماسة تلغي إشارة الإعطاء؛ لك الأولوية',
        },
      },
      {
        id: 'q-priority2-11o2',
        text: {
          en: 'The Give way sign applies; you must yield to traffic on the other road',
          nl: 'Het bord Voorrang verlenen geldt voor u; u moet verkeer op de andere weg voorrang verlenen',
          ar: 'تنطبق إشارة «أعط الأولوية»؛ يجب أن تمنح الأولوية للمرور على الطريق الآخر',
        },
      },
      {
        id: 'q-priority2-11o3',
        text: {
          en: 'Treat it as priority to the right only',
          nl: 'Behandel het als alleen voorrang van rechts',
          ar: 'عاملها على أنها الأولوية للقادم من اليمين فقط',
        },
      },
    ],
    correctAnswerId: 'q-priority2-11o2',
    explanation: {
      en: 'The Give way sign at the junction applies to you. You must yield to traffic on the road you are joining; the diamond may apply to the other road.',
      nl: 'Het bord Voorrang verlenen bij de kruising geldt voor u. U moet verkeer op de weg waar u op invoegt voorrang verlenen; het ruitvormige bord kan voor de andere weg gelden.',
      ar: 'إشارة «أعط الأولوية» عند التقاطع تنطبق عليك. يجب أن تمنح الأولوية للمرور على الطريق الذي تندمج إليه؛ قد تنطبق الماسة على الطريق الآخر.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-priority2-12',
    text: {
      en: 'You are waiting to turn right at a junction. An oncoming vehicle is also turning right. How should you pass?',
      nl: 'U wacht om rechtsaf te slaan bij een kruising. Een tegenligger slaat ook rechtsaf. Hoe moet u elkaar passeren?',
      ar: 'تنتظر لتنعطف يمينا عند تقاطع. مركبة قادمة من الاتجاه المعاكس تنعطف يمينا أيضا. كيف يجب أن تمرا؟',
    },
    options: [
      {
        id: 'q-priority2-12o1',
        text: {
          en: 'You both turn in front of each other (right side to right side)',
          nl: 'U slaat beiden voor elkaar af (rechterzijde langs rechterzijde)',
          ar: 'تمران أمام بعضكما (من الجانب الأيمن إلى الجانب الأيمن)',
        },
      },
      {
        id: 'q-priority2-12o2',
        text: {
          en: 'Whoever moves first has priority',
          nl: 'Wie het eerst beweegt, heeft voorrang',
          ar: 'من يتحرك أولا له الأولوية',
        },
      },
      {
        id: 'q-priority2-12o3',
        text: {
          en: 'The vehicle on the main road goes first',
          nl: 'Het voertuig op de hoofdweg gaat eerst',
          ar: 'المركبة على الطريق الرئيسي تمر أولا',
        },
      },
    ],
    correctAnswerId: 'q-priority2-12o1',
    explanation: {
      en: 'When two vehicles are turning right at a junction, they normally pass right side to right side so that neither crosses the other\'s path.',
      nl: 'Wanneer twee voertuigen bij een kruising rechtsaf slaan, passeren zij doorgaans rechterzijde langs rechterzijde, zodat geen van beiden het pad van de ander kruist.',
      ar: 'عندما تنعطف مركبتان يمينا عند تقاطع، يمران عادة من الجانب الأيمن إلى الجانب الأيمن حتى لا يقطع أحدهما مسار الآخر.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-ped-tram-priority',
    text: {
      en: 'What is the correct order of letting go?',
      nl: 'Wat is de juiste volgorde van doorlaten?',
      ar: 'ما هو الترتيب الصحيح للسماح بالمرور؟',
    },
    options: [
      {
        id: 'q-ped-tram-o1',
        text: {
          en: '1, 2',
          nl: '1, 2',
          ar: '1, 2',
        },
      },
      {
        id: 'q-ped-tram-o2',
        text: {
          en: '2, 1',
          nl: '2, 1',
          ar: '2, 1',
        },
      },
      {
        id: 'q-ped-tram-o3',
        text: {
          en: 'At the same time',
          nl: 'Tegelijkertijd',
          ar: 'في نفس الوقت',
        },
      },
    ],
    correctAnswerId: 'q-ped-tram-o1',
    explanation: {
      en: 'At a zebra crossing, pedestrians always have priority. The tram must give way to the pedestrian even though trams normally have priority over other road users (RVV 1990 art. 49). Pedestrian = 1, Tram = 2.',
      nl: 'Op een zebrapad hebben voetgangers altijd voorrang. De tram moet voorrang verlenen aan de voetganger, ook al hebben trams normaal gesproken voorrang op andere weggebruikers (RVV 1990 art. 49). Voetganger = 1, Tram = 2.',
      ar: 'عند ممر المشاة، للمشاة الأولوية دائما. يجب على الترام إعطاء الأولوية للمشاة حتى لو كان للترام في العادة الأولوية على سائر مستخدمي الطريق (RVV 1990 المادة 49). المشاة = 1، الترام = 2.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-tram-car-priority',
    text: {
      en: 'What is the correct order of letting go?',
      nl: 'Wat is de juiste volgorde van doorlaten?',
      ar: 'ما هو الترتيب الصحيح للسماح بالمرور؟',
    },
    options: [
      {
        id: 'q-tram-car-o1',
        text: {
          en: '2, 1',
          nl: '2, 1',
          ar: '2, 1',
        },
      },
      {
        id: 'q-tram-car-o2',
        text: {
          en: '1, 2',
          nl: '1, 2',
          ar: '1, 2',
        },
      },
      {
        id: 'q-tram-car-o3',
        text: {
          en: 'At the same time',
          nl: 'Tegelijkertijd',
          ar: 'في نفس الوقت',
        },
      },
    ],
    correctAnswerId: 'q-tram-car-o2',
    explanation: {
      en: 'Trams always have priority over all other road users (RVV 1990 art. 3). The car must give way to the tram regardless of any road markings or signs. Tram = 1, Car = 2.',
      nl: 'Trams hebben altijd voorrang op alle andere weggebruikers (RVV 1990 art. 3). De auto moet voorrang verlenen aan de tram, ongeacht wegmarkeringen of borden. Tram = 1, Auto = 2.',
      ar: 'للترام دائما الأولوية على جميع مستخدمي الطريق الآخرين (RVV 1990 المادة 3). يجب على السيارة إعطاء الأولوية للترام بغض النظر عن أي علامات أو إشارات على الطريق. الترام = 1، السيارة = 2.',
    },
    subject: 'Priority Rules',
  },
  {
    id: 'q-emergency-tram-car',
    text: {
      en: 'What is the correct order of letting go?',
      nl: 'Wat is de juiste volgorde van doorlaten?',
      ar: 'ما هو الترتيب الصحيح للسماح بالمرور؟',
    },
    options: [
      {
        id: 'q-emergency-tram-car-o1',
        text: {
          en: '1, 2, 3',
          nl: '1, 2, 3',
          ar: '1, 2, 3',
        },
      },
      {
        id: 'q-emergency-tram-car-o2',
        text: {
          en: '2, 1, 3',
          nl: '2, 1, 3',
          ar: '2, 1, 3',
        },
      },
      {
        id: 'q-emergency-tram-car-o3',
        text: {
          en: '3, 2, 1',
          nl: '3, 2, 1',
          ar: '3, 2, 1',
        },
      },
    ],
    correctAnswerId: 'q-emergency-tram-car-o1',
    explanation: {
      en: 'Emergency vehicles with blue lights and sirens have absolute priority over all road users (RVV 1990 art. 91). Trams have priority over regular vehicles (art. 3). Therefore: Emergency vehicle = 1, Tram = 2, Car = 3.',
      nl: 'Hulpdienstvoertuigen met blauwe zwaailichten en sirenes hebben absolute voorrang op alle weggebruikers (RVV 1990 art. 91). Trams hebben voorrang op gewone voertuigen (art. 3). Dus: Hulpdienstvoertuig = 1, Tram = 2, Auto = 3.',
      ar: 'مركبات الطوارئ ذات الأضواء الزرقاء والصفارات لها الأولوية المطلقة على جميع مستخدمي الطريق (RVV 1990 المادة 91). للترام الأولوية على المركبات العادية (المادة 3). إذن: مركبة الطوارئ = 1، الترام = 2، السيارة = 3.',
    },
    subject: 'Priority Rules',
  },
];
